import { onUnmounted, ref } from 'vue'

// Global timer registry to track all active timers
const activeTimers = new Set<number>()

export const useTimers = () => {
  const timers = ref<Set<number>>(new Set())

  const setInterval = (callback: () => void, delay: number): number => {
    const id = window.setInterval(() => {
      callback()
    }, delay)

    // Track timer
    timers.value.add(id)
    activeTimers.add(id)

    return id
  }

  const setTimeout = (callback: () => void, delay: number): number => {
    const id = window.setTimeout(() => {
      callback()

      // Remove from tracking when timeout completes
      timers.value.delete(id)
      activeTimers.delete(id)
    }, delay)

    // Track timer
    timers.value.add(id)
    activeTimers.add(id)

    return id
  }

  const clearInterval = (id: number) => {
    window.clearInterval(id)
    timers.value.delete(id)
    activeTimers.delete(id)
  }

  const clearTimeout = (id: number) => {
    window.clearTimeout(id)
    timers.value.delete(id)
    activeTimers.delete(id)
  }

  const clearAllTimers = () => {
    // Clear all timers in this composable instance
    timers.value.forEach(id => {
      window.clearInterval(id)
      window.clearTimeout(id)
    })
    timers.value.clear()
  }

  // Auto cleanup on unmount
  onUnmounted(() => {
    clearAllTimers()
  })

  return {
    setInterval,
    setTimeout,
    clearInterval,
    clearTimeout,
    clearAllTimers,
    activeTimers: timers,
  }
}

// Global function to clear all timers across the entire app
export const clearAllGlobalTimers = () => {
  activeTimers.forEach(id => {
    window.clearInterval(id)
    window.clearTimeout(id)
  })
  activeTimers.clear()
}
